import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { TerminalLockerLiveItem } from "../../../types/terminal-locker-live.type";
import { authApiService } from "../../../api/api";
import TerminalBleDevicesModal from "../TerminalBleDevicesModal/TerminalBleDevicesModal";
import styles from "./styles.module.scss";

interface TerminalLiveCardProps {
  terminal: TerminalLockerLiveItem;
  /** Після збереження адреси/координат — оновити список (наприклад poll). */
  onMetaSaved?: () => void | Promise<void>;
  /**
   * Якщо true — шапка з ID, статусом і BLE схована (дублює рядок гармошки
   * на сторінці терміналів).
   */
  embedInAccordion?: boolean;
}

function lockPillClass(
  lockStatus: "open" | "closed",
  stylesObj: typeof styles,
): string {
  return lockStatus === "closed"
    ? stylesObj.pillLockClosed
    : stylesObj.pillLockOpen;
}

function boolPillClass(value: boolean, stylesObj: typeof styles): string {
  return value ? stylesObj.pillGood : stylesObj.pillBad;
}

const TELEGRAM_BOT_USERNAME = "electrovel_terminals_bot";

function telegramDeepLink(code: string): string {
  const enc = encodeURIComponent(code);
  return `https://t.me/${TELEGRAM_BOT_USERNAME}?start=${enc}`;
}

function formatExpiresAt(ms: number): string {
  try {
    return new Date(ms).toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function formatApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[] }
      | undefined;
    const m = data?.message;
    if (Array.isArray(m)) {
      return m.join(", ");
    }
    if (typeof m === "string") {
      return m;
    }
    if (err.response?.status === 503) {
      return "Термінал не підключено до сервера.";
    }
  }
  return "Не вдалося виконати команду.";
}

const TerminalLiveCard: React.FC<TerminalLiveCardProps> = ({
  terminal,
  onMetaSaved,
  embedInAccordion = false,
}) => {
  const [confirmCellIndex, setConfirmCellIndex] = useState<number | null>(null);
  const [isOpening, setIsOpening] = useState(false);
  const tgCode = terminal.telegram_link_code?.trim() ?? "";

  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [draftAddress, setDraftAddress] = useState("");
  const [draftLat, setDraftLat] = useState("");
  const [draftLng, setDraftLng] = useState("");
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [bleModalOpen, setBleModalOpen] = useState(false);

  const displayAddress = terminal.address?.trim() || null;
  const displayLat = terminal.latitude;
  const displayLng = terminal.longitude;

  const beginEditMeta = () => {
    setDraftAddress(terminal.address?.trim() ?? "");
    setDraftLat(
      terminal.latitude != null && !Number.isNaN(terminal.latitude)
        ? String(terminal.latitude)
        : "",
    );
    setDraftLng(
      terminal.longitude != null && !Number.isNaN(terminal.longitude)
        ? String(terminal.longitude)
        : "",
    );
    setIsEditingMeta(true);
  };

  const cancelEditMeta = () => {
    if (isSavingMeta) return;
    setIsEditingMeta(false);
  };

  const handleSaveMeta = async () => {
    const addrTrim = draftAddress.trim();
    const addressPayload = addrTrim === "" ? null : addrTrim;

    const latTrim = draftLat.trim();
    const lngTrim = draftLng.trim();

    let latitude: number | null = null;
    let longitude: number | null = null;

    if (latTrim !== "") {
      const n = Number(latTrim.replace(",", "."));
      if (Number.isNaN(n) || n < -90 || n > 90) {
        toast.error("Широта має бути числом від −90 до 90.");
        return;
      }
      latitude = n;
    }
    if (lngTrim !== "") {
      const n = Number(lngTrim.replace(",", "."));
      if (Number.isNaN(n) || n < -180 || n > 180) {
        toast.error("Довгота має бути числом від −180 до 180.");
        return;
      }
      longitude = n;
    }

    setIsSavingMeta(true);
    try {
      const updated = await authApiService.updateTerminal(terminal.id, {
        address: addressPayload,
        latitude,
        longitude,
      });
      if (!updated) {
        toast.error("Потрібна авторизація. Увійдіть знову.");
        return;
      }
      toast.success("Адресу та координати збережено.");
      setIsEditingMeta(false);
      await onMetaSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleConfirmOpen = async () => {
    if (confirmCellIndex === null) return;
    setIsOpening(true);
    try {
      const res = await authApiService.openTerminalLockerCell(
        terminal.terminal_id,
        confirmCellIndex,
      );
      if (!res) {
        toast.error("Потрібна авторизація. Увійдіть знову.");
        return;
      }
      toast.success(
        `Команду на відкриття комірки ${confirmCellIndex} надіслано.`,
      );
      setConfirmCellIndex(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <section
      className={styles.card}
      data-embed-accordion={embedInAccordion || undefined}
    >
      {!embedInAccordion && (
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>{terminal.terminal_id}</h2>
            <span className={styles.sub}>
              Публічний ідентифікатор терміналу
            </span>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={styles.bleButton}
              onClick={() => setBleModalOpen(true)}
            >
              BLE пристрої
            </button>
            <span
              className={
                terminal.connected ? styles.badgeOnline : styles.badgeOffline
              }
            >
              {terminal.connected ? "Підключено (WebSocket)" : "Не в мережі"}
            </span>
          </div>
        </header>
      )}

      <div className={styles.metaBlock}>
        <div className={styles.metaTitle}>Локація</div>
        {!isEditingMeta ? (
          <>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Адреса</span>
              <span className={styles.metaValue} title={displayAddress ?? ""}>
                {displayAddress ?? "—"}
              </span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Координати</span>
              <span className={styles.metaValue}>
                {displayLat != null && displayLng != null
                  ? `${displayLat}, ${displayLng}`
                  : "—"}
              </span>
            </div>
            <button
              type="button"
              className={styles.metaEditButton}
              onClick={beginEditMeta}
            >
              Редагувати адресу та координати
            </button>
          </>
        ) : (
          <div className={styles.metaForm}>
            <label className={styles.metaField}>
              <span className={styles.metaLabel}>Адреса</span>
              <textarea
                className={styles.metaTextarea}
                value={draftAddress}
                onChange={(e) => setDraftAddress(e.target.value)}
                rows={3}
                placeholder="Вулиця, місто…"
              />
            </label>
            <div className={styles.metaCoords}>
              <label className={styles.metaField}>
                <span className={styles.metaLabel}>Широта</span>
                <input
                  type="text"
                  className={styles.metaInput}
                  inputMode="decimal"
                  value={draftLat}
                  onChange={(e) => setDraftLat(e.target.value)}
                  placeholder="50.45"
                />
              </label>
              <label className={styles.metaField}>
                <span className={styles.metaLabel}>Довгота</span>
                <input
                  type="text"
                  className={styles.metaInput}
                  inputMode="decimal"
                  value={draftLng}
                  onChange={(e) => setDraftLng(e.target.value)}
                  placeholder="30.52"
                />
              </label>
            </div>
            <p className={styles.metaHint}>
              WGS-84. Залиште широту/довготу порожніми, щоб скинути координати.
            </p>
            <div className={styles.metaActions}>
              <button
                type="button"
                className={styles.metaButtonSecondary}
                onClick={cancelEditMeta}
                disabled={isSavingMeta}
              >
                Скасувати
              </button>
              <button
                type="button"
                className={styles.metaButtonPrimary}
                onClick={handleSaveMeta}
                disabled={isSavingMeta}
              >
                {isSavingMeta ? "Збереження…" : "Зберегти"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* <div className={styles.telegramBlock}>
        <div className={styles.telegramTitle}>Telegram (QR / deep link)</div>
        {tgCode !== "" ? (
          <>
            <div className={styles.telegramRow}>
              <span className={styles.telegramLabel}>Код start</span>
              <code className={styles.telegramCode} title={tgCode}>
                {tgCode}
              </code>
            </div>
            {terminal.telegram_link_expires_at != null && (
              <div className={styles.telegramRow}>
                <span className={styles.telegramLabel}>Дійсний до</span>
                <span className={styles.telegramExpiry}>
                  {formatExpiresAt(terminal.telegram_link_expires_at)}
                </span>
              </div>
            )}
            <a
              className={styles.telegramLink}
              href={telegramDeepLink(tgCode)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Відкрити в Telegram
            </a>
          </>
        ) : (
          <p className={styles.telegramEmpty}>
            Немає активного коду: термінал ще не надіслав або код прострочений.
            Оновіть сторінку після підключення термінала.
          </p>
        )}
      </div> */}

      {terminal.cells.length === 0 ? (
        <p className={styles.empty}>
          Немає даних по комірках (очікуйте статус).
        </p>
      ) : (
        <ul className={styles.cellGrid}>
          {terminal.cells.map((cell) => (
            <li key={cell.cellIndex} className={styles.cell}>
              <div className={styles.cellTop}>
                <span className={styles.cellIndex}>{cell.cellIndex}</span>
                <span className={styles.cellTitle}>Комірка</span>
              </div>

              <div className={styles.cellBody}>
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Замок</span>
                  <span
                    className={`${styles.pill} ${lockPillClass(
                      cell.lockStatus,
                      styles,
                    )}`}
                  >
                    {cell.lockStatus === "closed" ? "Закрито" : "Відкрито"}
                  </span>
                </div>
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Акум. у комірці</span>
                  <span
                    className={`${styles.pill} ${boolPillClass(
                      cell.batteryConnected,
                      styles,
                    )}`}
                  >
                    {cell.batteryConnected ? "Так" : "Ні"}
                  </span>
                </div>
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Заряджено</span>
                  <span
                    className={`${styles.pill} ${boolPillClass(
                      cell.batteryCharged,
                      styles,
                    )}`}
                  >
                    {cell.batteryCharged ? "Так" : "Ні"}
                  </span>
                </div>

                <button
                  type="button"
                  className={styles.openButton}
                  disabled={!terminal.connected || isOpening}
                  onClick={() => setConfirmCellIndex(cell.cellIndex)}
                >
                  Відкрити комірку
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!embedInAccordion && (
        <TerminalBleDevicesModal
          terminal={terminal}
          isOpen={bleModalOpen}
          onClose={() => setBleModalOpen(false)}
          onMetaSaved={onMetaSaved}
        />
      )}

      {confirmCellIndex !== null && (
        <div
          className={styles.confirmBackdrop}
          onClick={() => !isOpening && setConfirmCellIndex(null)}
          role="presentation"
        >
          <div
            className={styles.confirmModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="open-cell-confirm-title"
          >
            <h3 id="open-cell-confirm-title" className={styles.confirmTitle}>
              Підтвердіть відкриття
            </h3>
            <p className={styles.confirmText}>
              Надіслати команду відкрити комірку{" "}
              <strong>№ {confirmCellIndex}</strong> на терміналі{" "}
              <strong>{terminal.terminal_id}</strong>? Переконайтеся, що біля
              шафи є персонал.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButtonSecondary}
                onClick={() => setConfirmCellIndex(null)}
                disabled={isOpening}
              >
                Скасувати
              </button>
              <button
                type="button"
                className={styles.confirmButtonPrimary}
                onClick={handleConfirmOpen}
                disabled={isOpening}
              >
                {isOpening ? "Надсилання…" : "Так, відкрити"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TerminalLiveCard;
